import { Component, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { UserAdressLocationFormComponent } from '../../user-profile/@components/user-address-card/@toasts/edit-user-adress-toast/user-adress-location-form/user-adress-location-form.component';
import { GardenBedCreateComponent } from '../garden-bed/garden-bed-create/garden-bed-create.component';
import { WizardHeaderComponent } from '../../@commons/ui-components/wizard-header/wizard-header.component';
import { GardenBedApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/garden-bed/garden-bed-api';
import { SharedModule } from '../../@commons/ui-components/shared.module';
import { LocationHomeIconComponent } from '../../@icons/location-home-icon.component';
import { BedCellsIconComponent } from '../../@icons/bed-cells-icon.component';
import { PotIconComponent } from '../../@icons/pot-icon.component';
import { DeleteIconComponent } from '../../@icons/delete-icon.component';
import { PlusIconComponent } from '../../@icons/plus-icon.component';

interface AddedBed {
  uid?: string;
  name: string;
  type: string;
  rows?: number;
  columns?: number;
  capacity?: number;
}

/**
 * Wizard de création de zone en 2 étapes :
 *  1. La zone (réutilise vw-user-adress-location-form).
 *  2. Les planches (réutilise vw-garden-bed-create inline, autoNavigate=false).
 * La zone est persistée à l'étape 1 ; un abandon à l'étape 2 laisse une zone valide sans planche.
 */
@Component({
  selector: 'vw-zone-create-wizard',
  imports: [TranslatePipe, SharedModule, UserAdressLocationFormComponent, GardenBedCreateComponent, WizardHeaderComponent, LocationHomeIconComponent, BedCellsIconComponent, PotIconComponent, DeleteIconComponent, PlusIconComponent],
  templateUrl: './zone-create-wizard.component.html',
  styleUrl: './zone-create-wizard.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: GardenBedApi, useClass: GardenBedApi }],
})
export class ZoneCreateWizardComponent {
  @Output() finished = new EventEmitter<any>();

  step: 1 | 2 = 1;
  createdLocation?: any;
  addedBeds: AddedBed[] = [];
  showAddBedSheet = false;
  deletingBedIndex: number | null = null;
  /** La dernière suppression de planche a échoué : la planche reste, le message le dit. */
  removeFailed = false;

  constructor(private gardenBedApi: GardenBedApi) {}

  onZoneCreated(location?: any): void {
    if (location?.uid) {
      this.createdLocation = location;
      this.step = 2;
    } else {
      // Annulation (back) depuis le formulaire de zone → on ferme le wizard.
      this.finished.emit(undefined);
    }
  }

  openAddBed(): void {
    this.showAddBedSheet = true;
  }

  closeAddBed(): void {
    this.showAddBedSheet = false;
  }

  onBedCreated(bed: any): void {
    this.addedBeds.push({
      uid: bed?.uid,
      name: bed?.name,
      type: bed?.type,
      rows: bed?.rows,
      columns: bed?.columns,
      capacity: bed?.capacity,
    });
    this.showAddBedSheet = false;
  }

  /** La planche vient d'être créée (vide) : la retirer du wizard = la supprimer côté API. */
  async removeBed(index: number): Promise<void> {
    const bed = this.addedBeds[index];
    if (!bed || this.deletingBedIndex !== null) {
      return;
    }
    this.deletingBedIndex = index;
    this.removeFailed = false;
    try {
      if (bed.uid) {
        await this.gardenBedApi.deleteById(bed.uid);
      }
      this.addedBeds.splice(index, 1);
    } catch (error) {
      console.error('Planche non supprimée', error);
      this.removeFailed = true;
    } finally {
      this.deletingBedIndex = null;
    }
  }

  /**
   * La croix : à l'étape 1 rien n'est créé, on ferme sans recharger ; à
   * l'étape 2 la zone existe déjà, on ferme comme « Terminer ».
   */
  close(): void {
    if (this.step === 1) {
      this.finished.emit(undefined);
    } else {
      this.finish();
    }
  }

  finish(): void {
    // On renvoie la zone créée pour signaler au parent qu'il faut recharger le potager.
    this.finished.emit(this.createdLocation);
  }
}
